import React, { useEffect, useId, useState } from 'react';
import { createPortal } from 'react-dom';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import patientService from '../../../services/patientService';
import { validatePassword } from '../../../utils/passwordValidation';
import QRCode from 'qrcode';
import { EVITALS_LOGO_BASE64 } from '../../../assets/evitalsLogoBase64';
import playStoreBadgeUrl from '../../../assets/store-badges/google-play-badge.png';
import appStoreBadgeUrl from '../../../assets/store-badges/app-store-badge.png';

const PASSWORD_RULES = [
  { label: 'Minimum 8 characters', test: (value) => value.length >= 8 },
  { label: 'At least 1 uppercase letter', test: (value) => /[A-Z]/.test(value) },
  { label: 'At least 1 lowercase letter', test: (value) => /[a-z]/.test(value) },
  { label: 'At least 1 number', test: (value) => /[0-9]/.test(value) },
  { label: 'At least 1 special character (!@#$%^&*)', test: (value) => /[!@#$%^&*]/.test(value) },
];

const safeFilePart = (value) => String(value || 'patient')
  .replace(/[^\w.-]+/g, '-')
  .replace(/-+/g, '-')
  .replace(/^-|-$/g, '')
  .slice(0, 40) || 'patient';

const NAVY = [3, 4, 94];
const SLATE = [71, 85, 105];
const INK = [15, 23, 42];
const LINE = [203, 213, 225];
const ROW = [248, 250, 252];
const HIGHLIGHT = [238, 242, 255];
const APP_DOWNLOAD_URL = 'https://evitals.life/api/mobile-app';
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.evitalsrpmapp';
const APP_STORE_URL = 'https://apps.apple.com/app/id6752558130';

const loadRaster = (src) => new Promise((resolve, reject) => {
  const image = new Image();
  image.onload = () => resolve(image);
  image.onerror = () => reject(new Error('badge'));
  image.src = src;
});

let logoImagePromise;
const loadLogo = () => {
  if (!logoImagePromise) {
    logoImagePromise = new Promise((resolve, reject) => {
      const image = new Image();
      image.onload = () => resolve(image);
      image.onerror = () => reject(new Error('logo'));
      image.src = EVITALS_LOGO_BASE64;
    });
  }
  return logoImagePromise;
};

const createSignInPdf = async ({ patientName, username, password, practiceName, includeStoreBadges = false }) => {
  const pdf = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const left = 16;
  const right = pageWidth - 16;
  const contentWidth = right - left;
  const dateLabel = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  let headerBottom = 28;
  try {
    const logo = await loadLogo();
    const logoHeight = 16;
    const logoWidth = Math.min(62, (logo.naturalWidth / logo.naturalHeight) * logoHeight);
    pdf.addImage(EVITALS_LOGO_BASE64, 'PNG', left, 12, logoWidth, logoHeight);
    headerBottom = 12 + logoHeight;
  } catch {
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(18);
    pdf.setTextColor(...NAVY);
    pdf.text('E-Vitals', left, 24);
    headerBottom = 28;
  }

  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(11);
  pdf.setTextColor(...NAVY);
  pdf.text('PATIENT SIGN-IN', right, 18, { align: 'right' });
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(9);
  pdf.setTextColor(...SLATE);
  pdf.text(dateLabel, right, 24, { align: 'right' });

  const ruleY = headerBottom + 6;
  pdf.setDrawColor(...NAVY);
  pdf.setLineWidth(0.6);
  pdf.line(left, ruleY, right, ruleY);

  let y = ruleY + 12;
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(16);
  pdf.setTextColor(...INK);
  pdf.text('Sign-in details', left, y);
  y += 7;
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(10);
  pdf.setTextColor(...SLATE);
  const intro = pdf.splitTextToSize(
    'Use the credentials below to sign in to the E-Vitals mobile app.',
    contentWidth
  );
  pdf.text(intro, left, y);
  y += intro.length * 5 + 4;

  autoTable(pdf, {
    startY: y,
    margin: { left, right: 16 },
    tableWidth: contentWidth,
    head: [['Detail', 'Value']],
    body: [
      ['Patient', patientName || '—'],
      ['Practice', practiceName || '—'],
      ['Username', username || '—'],
      ['Password', password || '—']
    ],
    theme: 'grid',
    styles: {
      font: 'helvetica',
      fontSize: 11,
      textColor: INK,
      lineColor: LINE,
      lineWidth: 0.25,
      cellPadding: { top: 4.2, right: 4, bottom: 4.2, left: 4 },
      valign: 'middle',
      overflow: 'linebreak',
      minCellHeight: 12
    },
    headStyles: {
      fillColor: NAVY,
      textColor: [255, 255, 255],
      fontStyle: 'bold',
      fontSize: 10,
      halign: 'left'
    },
    columnStyles: {
      0: { cellWidth: 42, fontStyle: 'bold', textColor: SLATE, fillColor: ROW },
      1: { cellWidth: contentWidth - 42, fontStyle: 'bold', textColor: INK }
    },
    didParseCell: (data) => {
      if (data.section !== 'body') return;
      const emphasized = data.row.index === 2 || data.row.index === 3;
      if (emphasized && data.column.index === 0) {
        data.cell.styles.fillColor = HIGHLIGHT;
        data.cell.styles.textColor = NAVY;
      }
      if (emphasized && data.column.index === 1) {
        data.cell.styles.fillColor = HIGHLIGHT;
        data.cell.styles.textColor = NAVY;
        data.cell.styles.fontSize = 12;
      }
    }
  });

  const afterTable = (pdf.lastAutoTable?.finalY || y) + 10;
  let appsY = afterTable;
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(13);
  pdf.setTextColor(...INK);
  pdf.text('Download the E-Vitals mobile app', left, appsY);
  appsY += 6;

  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(10);
  pdf.setTextColor(...SLATE);
  const caption = pdf.splitTextToSize(
    'Scan this code with the patient\'s phone. Android opens Google Play. iPhone opens the App Store.',
    contentWidth
  );
  pdf.text(caption, left, appsY);
  appsY += caption.length * 5 + 4;

  if (includeStoreBadges) {
    const [playImage, appleImage] = await Promise.all([
      loadRaster(playStoreBadgeUrl),
      loadRaster(appStoreBadgeUrl)
    ]);
    const badgeHeight = 10;
    const playWidth = badgeHeight * (playImage.naturalWidth / playImage.naturalHeight);
    const appleWidth = badgeHeight * (appleImage.naturalWidth / appleImage.naturalHeight);
    const columnGap = 4;
    pdf.addImage(playImage, 'PNG', left, appsY, playWidth, badgeHeight);
    pdf.addImage(appleImage, 'PNG', left + playWidth + columnGap, appsY, appleWidth, badgeHeight);
    pdf.link(left, appsY, playWidth, badgeHeight, { url: PLAY_STORE_URL });
    pdf.link(left + playWidth + columnGap, appsY, appleWidth, badgeHeight, { url: APP_STORE_URL });
  } else {
    const qrSize = 38;
    const qrDataUrl = await QRCode.toDataURL(APP_DOWNLOAD_URL, {
      errorCorrectionLevel: 'Q',
      margin: 1,
      width: 512,
      color: { dark: '#0f172a', light: '#ffffff' }
    });
    pdf.addImage(qrDataUrl, 'PNG', left, appsY, qrSize, qrSize);
    pdf.link(left, appsY, qrSize, qrSize, { url: APP_DOWNLOAD_URL });
  }

  pdf.setDrawColor(...LINE);
  pdf.setLineWidth(0.3);
  pdf.line(left, pageHeight - 16, right, pageHeight - 16);
  pdf.setFontSize(8);
  pdf.setTextColor(148, 163, 184);
  pdf.text('E-Vitals', left, pageHeight - 10);
  pdf.text('Confidential', right, pageHeight - 10, { align: 'right' });

  return pdf;
};

const saveSignInPdf = async (details) => {
  const pdf = await createSignInPdf(details);
  pdf.save(`E-Vitals-sign-in-${safeFilePart(details.patientName)}.pdf`);
};

const buildEmailSignInPdfBase64 = async (details) => {
  const pdf = await createSignInPdf({ ...details, includeStoreBadges: true });
  const dataUri = pdf.output('datauristring');
  const marker = 'base64,';
  const index = dataUri.indexOf(marker);
  return index >= 0 ? dataUri.slice(index + marker.length) : dataUri;
};

const IconKey = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <circle cx="8" cy="15" r="4" />
    <path d="M11.5 12.5L20 4" />
    <path d="M16 8l3 3" />
    <path d="M18 6l2 2" />
  </svg>
);

const EyeIcon = ({ off }) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    {off ? (
      <>
        <path d="M3 3l18 18" />
        <path d="M10.6 10.6a2 2 0 0 0 2.8 2.8" />
        <path d="M9.9 5.1A10.8 10.8 0 0 1 12 5c5 0 9.3 3.1 11 7.5a11.6 11.6 0 0 1-4.2 5.2" />
        <path d="M6.1 6.1C4.2 7.4 2.7 9.3 1.8 11.5 3.5 15.9 7.8 19 12.8 19c1.1 0 2.2-.2 3.2-.5" />
      </>
    ) : (
      <>
        <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7S1 12 1 12z" />
        <circle cx="12" cy="12" r="3" />
      </>
    )}
  </svg>
);

const PasswordField = ({ id, label, value, onChange, error, autoFocus = false, showRules = false }) => {
  const [visible, setVisible] = useState(false);
  return (
    <div className="pdx-reset-field">
      <label htmlFor={id}>{label}</label>
      <div className={`pdx-reset-input-wrap${error ? ' is-invalid' : ''}`}>
        <input
          id={id}
          type={visible ? 'text' : 'password'}
          value={value}
          autoFocus={autoFocus}
          autoComplete="new-password"
          placeholder="Enter at least 8 characters"
          onChange={(event) => onChange(event.target.value)}
          aria-invalid={error ? 'true' : 'false'}
          aria-describedby={error ? `${id}-error` : undefined}
        />
        <button
          type="button"
          className="pdx-reset-eye"
          onClick={() => setVisible((current) => !current)}
          aria-label={visible ? `Hide ${label}` : `Show ${label}`}
        >
          <EyeIcon off={visible} />
        </button>
      </div>
      {showRules ? (
        <ul className="pdx-reset-rules">
          {PASSWORD_RULES.map((rule) => {
            const met = rule.test(value);
            return (
              <li key={rule.label} className={met ? 'met' : ''}>
                <span aria-hidden="true">{met ? '✓' : '○'}</span>
                {rule.label}
              </li>
            );
          })}
        </ul>
      ) : null}
      {error ? <span className="pdx-reset-error" id={`${id}-error`}>{error}</span> : null}
    </div>
  );
};

const ResetPatientPasswordModal = ({
  open,
  onClose,
  practiceId,
  patientId,
  patientName,
  username,
  practiceName,
  firstName = '',
  lastName = '',
}) => {
  const titleId = useId();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState('');

  useEffect(() => {
    if (!open) return undefined;
    setPassword('');
    setConfirmPassword('');
    setErrors({});
    setFormError('');
    setSaving('');
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  const handlePasswordChange = (value) => {
    setPassword(value);
    setErrors((current) => ({ ...current, password: '' }));
    setFormError('');
  };

  const handleConfirmChange = (value) => {
    setConfirmPassword(value);
    setErrors((current) => ({ ...current, confirmPassword: '' }));
    setFormError('');
  };

  const handleSave = async (mode) => {
    const nextErrors = {};
    const passwordCheck = validatePassword(password, { firstName, lastName });
    if (!passwordCheck.isValid) nextErrors.password = passwordCheck.error;
    if (!confirmPassword) nextErrors.confirmPassword = 'Confirm the password.';
    else if (password !== confirmPassword) nextErrors.confirmPassword = 'Password and confirm password do not match.';
    if (!username) {
      setFormError('This patient does not have a sign-in username.');
      return;
    }
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      return;
    }

    setSaving(mode);
    setFormError('');
    try {
      let signInPdf = '';
      if (mode === 'email') {
        signInPdf = await buildEmailSignInPdfBase64({
          username,
          patientName,
          practiceName,
          password
        });
      }
      const response = await patientService.resetPatientPassword(practiceId, patientId, {
        password,
        confirmPassword,
        emailPatient: mode === 'email',
        signInPdf
      });
      const data = response?.data?.data || {};
      if (mode === 'pdf') {
        await saveSignInPdf({
          username: data.username || username,
          patientName: data.patientName || patientName,
          practiceName: data.practiceName || practiceName,
          password
        });
      }
      onClose();
    } catch (error) {
      const message = error?.response?.data?.message || 'Could not reset the password.';
      const field = error?.response?.data?.field;
      if (field === 'confirmPassword') {
        setErrors({ confirmPassword: message });
      } else if (field === 'password') {
        setErrors({ password: message });
      } else {
        setFormError(message);
      }
    } finally {
      setSaving('');
    }
  };

  return createPortal(
    <div className="pdx-reset-backdrop" role="presentation" onMouseDown={onClose}>
      <div
        className="pdx-reset-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        data-reset-password-modal="true"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="pdx-reset-head">
          <h2 id={titleId}>Reset password</h2>
          <button type="button" className="pdx-reset-close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>
        
        <form
          onSubmit={(event) => {
            event.preventDefault();
            handleSave('email');
          }}
        >
          <PasswordField
            id="patient-reset-password"
            label="New Password"
            value={password}
            onChange={handlePasswordChange}
            error={errors.password}
            autoFocus
            showRules
          />
          <PasswordField
            id="patient-reset-confirm"
            label="Confirm New Password"
            value={confirmPassword}
            onChange={handleConfirmChange}
            error={errors.confirmPassword}
          />
          {formError ? <p className="pdx-reset-form-error">{formError}</p> : null}
          <div className="pdx-reset-actions">
            <button type="button" className="pdx-chip" onClick={onClose} disabled={Boolean(saving)}>
              Cancel
            </button>
            <button type="submit" className="pdx-chip solid" disabled={Boolean(saving)}>
              {saving === 'email' ? 'Sending…' : 'Save and email'}
            </button>
            <button
              type="button"
              className="pdx-chip"
              disabled={Boolean(saving)}
              onClick={() => handleSave('pdf')}
            >
              {saving === 'pdf' ? 'Saving…' : 'Save and download PDF'}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
};

export { IconKey };
export default ResetPatientPasswordModal;
